import React, { useCallback, useEffect, useMemo } from 'react';

import { Controller, useForm } from 'react-hook-form';
import dayjs from '~/timeInit';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';

import { Input, Select } from '~/components/Common/Forms/fields';

import { POINTS_PURCHASE_OFFER_WRITABLE_STATUSES } from '~/consts/luxLoyalty';

import { useUpdatePointsPurchaseOffer } from '~/hooks/apis/loyalty/usePointsPurchaseMutations';

import type { PointsPurchaseOffer, PointsPurchaseOfferWritableStatus } from '~/services/LuxLoyalty';

import { toStoredStatus } from './utils';

interface EditOfferDialogProps {
  open: boolean;
  onClose: () => void;
  offer: PointsPurchaseOffer;
}

interface EditOfferFormData {
  title: string;
  validFrom: string;
  status: PointsPurchaseOfferWritableStatus;
}

function buildDefaultValues(offer: PointsPurchaseOffer): EditOfferFormData {
  return {
    title: offer.title,
    validFrom: offer.validFrom ? dayjs(offer.validFrom).format('YYYY-MM-DDTHH:mm') : '',
    status: toStoredStatus(offer.status),
  };
}

export default function EditOfferDialog({ open, onClose, offer }: EditOfferDialogProps) {
  const updateMutation = useUpdatePointsPurchaseOffer();

  const defaultValues = useMemo(() => buildDefaultValues(offer), [offer]);

  const { control, handleSubmit, reset } = useForm<EditOfferFormData>({
    defaultValues,
    mode: 'onBlur',
  });

  useEffect(() => {
    if (open && offer) {
      reset(buildDefaultValues(offer));
    }
  }, [open, offer, reset]);

  const onSubmit = useCallback(
    async (data: EditOfferFormData) => {
      if (!offer) {
        return;
      }
      try {
        await updateMutation.mutateAsync({
          offerId: offer.id,
          params: {
            title: data.title.trim(),
            validFrom: dayjs(data.validFrom).toISOString(),
            status: data.status,
          },
        });
        onClose();
      } catch {
        // Error handled in mutation hook
      }
    },
    [offer, updateMutation, onClose],
  );

  if (!offer) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Points Purchase Offer</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Base Points" value={offer.basePoints.toLocaleString()} disabled fullWidth />
          <TextField label="Price" value={offer.price} disabled fullWidth helperText="Cannot be changed" />
          <TextField label="Currency" value={offer.currency} disabled fullWidth />
          <Input
            control={control}
            name="title"
            muiLabel="Title"
            rules={{ required: 'Title is required' }}
            fullWidth
            required
          />
          <Controller
            name="validFrom"
            control={control}
            rules={{ required: 'Valid from date is required' }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Valid From"
                type="datetime-local"
                fullWidth
                required
                error={!!error}
                helperText={error?.message}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}
          />
          <Select control={control} name="status" muiLabel="Status" rules={{ required: 'Status is required' }}>
            {POINTS_PURCHASE_OFFER_WRITABLE_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={updateMutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
